import { ChevronLeft, ChevronRight, Palette, LogOut, Cloud, Settings, UserRound } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { periodLabel, shift } from "@/lib/money";
import { t } from "@/lib/i18n";
import { Brand } from "@/components/mm/Brand";

const MODES = [["week", "Week"], ["month", "Month"], ["year", "Year"]];

export function Avatar({ user, className = "h-10 w-10" }) {
  const ini = (user.name || user.email || "?").slice(0, 1).toUpperCase();
  return (
    <span className={`grid shrink-0 place-items-center overflow-hidden rounded-full border border-[var(--bd)] bg-[var(--card)] font-bold ${className}`}>
      {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" /> : ini}
    </span>
  );
}

function UserMenu({ onSettings }) {
  const { user, signOut } = useAuth();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="rounded-full" data-testid="header-user-profile-button" aria-label="Account"><Avatar user={user} /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="mm-dialog w-60">
        <DropdownMenuLabel>
          <div className="truncate font-semibold" data-testid="header-user-name">{user.name}</div>
          <div className="truncate text-xs font-normal text-[var(--mu)]">{user.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[var(--bd)]" />
        <DropdownMenuItem onClick={() => onSettings("profile")} className="cursor-pointer" data-testid="header-edit-profile-button"><UserRound className="mr-2 h-4 w-4" /> {t("Profile")}</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onSettings("general")} className="cursor-pointer" data-testid="header-menu-settings-button"><Settings className="mr-2 h-4 w-4" /> {t("Settings")}</DropdownMenuItem>
        <DropdownMenuItem onClick={signOut} className="cursor-pointer" data-testid="header-logout-button"><LogOut className="mr-2 h-4 w-4" /> {t("Sign out")}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const IconBtn = ({ onClick, testId, label, children }) => (
  <button className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[var(--bd)] bg-[var(--card)] transition-transform hover:-translate-y-0.5" onClick={onClick} data-testid={testId} aria-label={label} title={label}>{children}</button>
);

export default function Header({ onDesign, onSettings }) {
  const { mode, setMode, anchor, setAnchor, period, saving } = useData();
  return (
    <header className="mm-header sticky top-0 z-40 pt-[env(safe-area-inset-top)]">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-[max(1rem,env(safe-area-inset-left))] py-3 sm:gap-3 sm:px-6">
        <div className="mr-auto flex min-w-0 items-center gap-2" data-testid="header-logo">
          <Brand />
          <span className={`flex items-center gap-1 text-[11px] font-medium transition-opacity ${saving ? "opacity-100" : "opacity-50"} text-[var(--mu)]`} data-testid="header-sync-status">
            <Cloud className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{saving ? t("Saving…") : t("Saved")}</span>
          </span>
        </div>
        <div className="order-3 flex w-full min-w-0 items-center justify-between gap-1 sm:order-none sm:w-auto sm:gap-2">
          <div className="mm-seg shrink-0">
            {MODES.map(([k, l]) => (
              <button key={k} className={`${mode === k ? "on" : ""} !px-2.5 sm:!px-[.9rem]`} onClick={() => setMode(k)} data-testid={`header-period-${k}-button`}>{t(l)}</button>
            ))}
          </div>
          <div className="flex min-w-0 items-center">
            <button className="grid h-9 w-8 shrink-0 place-items-center rounded-full hover:bg-[var(--card)]" onClick={() => setAnchor(shift(mode, anchor, -1))} data-testid="header-prev-period-button" aria-label="Previous"><ChevronLeft className="h-5 w-5" /></button>
            <span className="num min-w-0 truncate text-center text-[11px] font-bold sm:min-w-[9.5rem] sm:text-[13px]" data-testid="header-period-label">{periodLabel(mode, period[0], period[1])}</span>
            <button className="grid h-9 w-8 shrink-0 place-items-center rounded-full hover:bg-[var(--card)]" onClick={() => setAnchor(shift(mode, anchor, 1))} data-testid="header-next-period-button" aria-label="Next"><ChevronRight className="h-5 w-5" /></button>
          </div>
        </div>
        <IconBtn onClick={onDesign} testId="header-design-button" label={t("Design")}><Palette className="h-4 w-4" /></IconBtn>
        <IconBtn onClick={() => onSettings("general")} testId="header-settings-button" label={t("Settings")}><Settings className="h-4 w-4" /></IconBtn>
        <UserMenu onSettings={onSettings} />
      </div>
    </header>
  );
}
